// Countertops/Cabinets manual estimate — pure helpers for the OrderConfirmation-shaped material &
// labor item model. The C/C quantity page renders the same Material/Labor tables as
// OrderConfirmation.jsx and pulls rows in via the shared PricingContract "Add Material/Labor"
// modal, so line items carry that screen's fields (quantity, unit/unitName, privateName, style,
// color, unitPrice, price, linePrice, gpm, lineCost, laborCategory, labor/laborName, ...).
//
// GPM convention mirrors OrderConfirmation exactly: per-line job cost is `lineCost` when present,
// otherwise derived from the entered gpm as linePrice x (100 - gpm) / 100.

const toNumber = (v) => Number(String(v ?? "").trim());
const num = (v) => {
  const n = toNumber(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round(n * 100) / 100;

// Effective unit price for a line (unitPrice, falling back to price — OrderConfirmation convention).
export function effectiveUnitPrice(item = {}) {
  const up = toNumber(item.unitPrice);
  if (!Number.isNaN(up) && String(item.unitPrice ?? "").trim() !== "") return up;
  return num(item.price);
}

// Line extended price = qty x effective unit price.
export function lineExtended(item = {}) {
  return round2(num(item.quantity) * effectiveUnitPrice(item));
}

// Return the item with linePrice recomputed (2dp string, matching the grid's display).
export function recalcLinePrice(item = {}) {
  return { ...item, linePrice: lineExtended(item).toFixed(2) };
}

// Per-line job cost. Precedence:
//   1. qty x unitCost   — product/kit rows carry a per-unit cost (currentCost); cost scales with qty
//   2. lineCost         — an explicit per-line cost
//   3. linePrice x (100 - gpm) / 100  — derive from the entered GPM (OrderConfirmation fallback)
export function lineJobCost(item = {}) {
  if (item.unitCost !== undefined && item.unitCost !== null && String(item.unitCost).trim() !== "") {
    return round2(num(item.quantity) * num(item.unitCost));
  }
  if (item.lineCost !== undefined && item.lineCost !== null && String(item.lineCost).trim() !== "") {
    return round2(num(item.lineCost));
  }
  const linePrice = String(item.linePrice ?? "").trim() !== "" ? num(item.linePrice) : lineExtended(item);
  return round2((linePrice * (100 - num(item.gpm))) / 100);
}

// Effective line price for totals: stored linePrice when present, else recomputed qty x unitPrice.
function effectiveLinePrice(item = {}) {
  return String(item.linePrice ?? "").trim() !== "" ? num(item.linePrice) : lineExtended(item);
}

// Roll up a section's items into { unitPriceTotal, linePriceTotal, cost, profit, gpm }.
export function sectionTotals(items = []) {
  const list = Array.isArray(items) ? items : [];
  const unitPriceTotal = round2(list.reduce((s, i) => s + effectiveUnitPrice(i), 0));
  const linePriceTotal = round2(list.reduce((s, i) => s + effectiveLinePrice(i), 0));
  const cost = round2(list.reduce((s, i) => s + lineJobCost(i), 0));
  const profit = round2(linePriceTotal - cost);
  const gpm = linePriceTotal > 0 ? round2((profit / linePriceTotal) * 100) : 0;
  return { unitPriceTotal, linePriceTotal, cost, profit, gpm };
}

// Validate a single line's qty + price for Save.
//   quantity  — required, numeric, > 0
//   unitPrice — required (falls back to `price`), numeric, >= 0 (a $0 line is allowed)
// Returns an object with only the failing fields set to a message ({} when the line is valid).
function validateLine(item = {}) {
  const errors = {};
  const qtyRaw = String(item.quantity ?? "").trim();
  const qty = toNumber(qtyRaw);
  if (qtyRaw === "") errors.quantity = "Qty is required";
  else if (!Number.isFinite(qty)) errors.quantity = "Qty must be a number";
  else if (qty <= 0) errors.quantity = "Qty must be greater than 0";

  const hasUnitPrice = String(item.unitPrice ?? "").trim() !== "";
  const hasPrice = String(item.price ?? "").trim() !== "";
  if (!hasUnitPrice && !hasPrice) {
    errors.unitPrice = "Price is required";
  } else {
    const priceRaw = hasUnitPrice ? String(item.unitPrice).trim() : String(item.price).trim();
    const price = toNumber(priceRaw);
    if (!Number.isFinite(price)) errors.unitPrice = "Price must be a number";
    else if (price < 0) errors.unitPrice = "Price cannot be negative";
  }
  return errors;
}

// Validate all material + labor lines. Returns
// { isValid, material: { [index]: {quantity?, unitPrice?} }, labor: {...} } — only invalid lines appear.
export function validateEstimateItems(materialList = [], laborList = []) {
  const collect = (list) => {
    const out = {};
    (Array.isArray(list) ? list : []).forEach((item, index) => {
      const errs = validateLine(item);
      if (Object.keys(errs).length) out[index] = errs;
    });
    return out;
  };
  const material = collect(materialList);
  const labor = collect(laborList);
  const isValid = Object.keys(material).length === 0 && Object.keys(labor).length === 0;
  return { isValid, material, labor };
}

// Save requires at least one material or labor line — an estimate with both grids empty has
// nothing to persist.
export function hasEstimateItems(materialList = [], laborList = []) {
  return (
    (Array.isArray(materialList) ? materialList : []).length > 0 ||
    (Array.isArray(laborList) ? laborList : []).length > 0
  );
}

// --- Add Scope de-duplication -------------------------------------------------------------------
// The "Add Scope" modal can be run repeatedly, and the same structure/product may be picked again
// in a later run (e.g. Cabinet Boxes chosen in two sessions). Identify a row by its structure +
// product/labor identity (plus name/style/color as a guard when ids are missing) so re-picking it
// updates the existing line instead of duplicating it — the LATEST selection wins.
const str = (v) => String(v ?? "").trim().toLowerCase();

export function materialIdentityKey(item = {}) {
  return [
    "m",
    num(item.productStructureId),
    num(item.productId),
    num(item.materialId),
    str(item.privateName),
    str(item.style),
    str(item.color),
  ].join("|");
}

export function laborIdentityKey(item = {}) {
  return [
    "l",
    num(item.productStructureId),
    num(item.laborId),
    num(item.laborItemId),
    str(item.labor ?? item.laborName),
    str(item.laborCategory),
  ].join("|");
}

// Merge `incoming` rows into `existing`, keyed by identity: a row whose key already appears (in the
// existing list or earlier in the same batch) REPLACES that row in place (keeping its position);
// genuinely new rows are appended. The latest selection wins, so re-adding a product shows its
// newest values without duplicating the line.
export function upsertUnique(existing = [], incoming = [], keyFn) {
  const base = Array.isArray(existing) ? existing : [];
  const add = Array.isArray(incoming) ? incoming : [];
  const indexByKey = new Map();
  const result = [...base];
  result.forEach((item, i) => indexByKey.set(keyFn(item), i));
  add.forEach((item) => {
    const key = keyFn(item);
    if (indexByKey.has(key)) {
      result[indexByKey.get(key)] = item; // replace in place — latest wins
    } else {
      indexByKey.set(key, result.length);
      result.push(item);
    }
  });
  return result;
}

// --- Kit quantity calculation ----------------------------------------------------------------
// When the estimator keys Qty on a kit's PARENT product row (isPrimary, from Add Scope), every
// add-on row for that kit is auto-calculated using a two-pass unit-type chain:
//
//   Pass 1 — "EA items" (SF master → EA addon):
//     ceil(masterSF / coverage)   e.g. 60 SF ÷ 32 SF/EA = ceil(1.875) = 2 EA
//
//   Pass 2 — "Derived items" (non-SF, non-EA addons such as LM or LF):
//     eaQty × coverage            e.g. 2 EA × 2.43 LM/EA = 4.86 LM
//
// Both Shop Supplies (EA) and Grey Thermofoil (LM) are direct peer addons in the same kit —
// there is no sub-structure parent/child relationship between them. The unit type (EA vs LM)
// is the only signal needed to determine which formula to apply.
//
// The estimator can still edit any add-on Qty after auto-calculation.
export function syncKitAddonQuantity(materialList = [], laborList = [], kitId, quantity, masterUnit = "") {
  const id = num(kitId);
  if (!id) return { material: materialList, labor: laborList };

  const masterQty = num(quantity);
  const mUnit = String(masterUnit ?? "").trim().toUpperCase();
  const unitOf = (item) => String(item.unit ?? item.unitName ?? "").trim().toUpperCase();

  const isAddon = (item) => !item.isPrimary && num(item.kitId) === id;
  // EA items: coverage-division from the SF master.
  const isEaItem = (item) => isAddon(item) && mUnit === "SF" && unitOf(item) === "EA";
  // Derived items: LM/LF/etc. with a coverage rate, chained from the EA result.
  const isDerived = (item) =>
    isAddon(item) && mUnit === "SF" && !["SF", "EA"].includes(unitOf(item)) && num(item.coverage) > 0;

  const eaQty = (item) => {
    const cov = num(item.coverage);
    return cov > 0 ? Math.ceil(masterQty / cov) : round2(masterQty * (num(item.quantityPerMaster) || 1));
  };

  const derivedQty = (item, allMat, allLab) => {
    // Locate the EA item already computed in Pass 1.
    const eaItem = [...allMat, ...allLab].find((t) => isEaItem(t));
    const parentQty = eaItem ? num(eaItem.quantity) : 0;
    return round2(parentQty * num(item.coverage));
  };

  // Pass 1: EA addons get coverage-divided qty (SF master only); all other addons that are
  // not coverage-derived (isDerived, handled in Pass 2) get a direct copy of the master qty.
  const applyPass1 = (item) => {
    if (isEaItem(item)) return recalcLinePrice({ ...item, quantity: String(eaQty(item)) });
    if (isAddon(item) && !isDerived(item)) return recalcLinePrice({ ...item, quantity: String(masterQty) });
    return item;
  };
  const mat1 = (Array.isArray(materialList) ? materialList : []).map(applyPass1);
  const lab1 = (Array.isArray(laborList) ? laborList : []).map(applyPass1);

  // Pass 2: derive LM/LF/etc. quantities from the Pass-1 EA result.
  const mat2 = mat1.map((item) =>
    isDerived(item) ? recalcLinePrice({ ...item, quantity: String(derivedQty(item, mat1, lab1)) }) : item
  );
  const lab2 = lab1.map((item) =>
    isDerived(item) ? recalcLinePrice({ ...item, quantity: String(derivedQty(item, mat1, lab1)) }) : item
  );

  return { material: mat2, labor: lab2 };
}

// When the estimator manually edits an EA addon qty (e.g. Shop Supplies changed to 2.5),
// re-derive all LM/LF/etc. items in the same kit from the new EA value × their coverage.
export function syncDerivedFromEaAddon(materialList = [], laborList = [], kitId, eaQty) {
  const id = num(kitId);
  if (!id) return { material: materialList, labor: laborList };

  const eaQtyNum = num(eaQty);
  const unitOf = (item) => String(item.unit ?? item.unitName ?? "").trim().toUpperCase();
  const isDerived = (item) =>
    !item.isPrimary && num(item.kitId) === id && !["SF", "EA"].includes(unitOf(item)) && num(item.coverage) > 0;

  const recompute = (list) =>
    (Array.isArray(list) ? list : []).map((item) =>
      isDerived(item)
        ? recalcLinePrice({ ...item, quantity: String(round2(eaQtyNum * num(item.coverage))) })
        : item
    );

  return { material: recompute(materialList), labor: recompute(laborList) };
}

// Estimate total = all material + labor line prices.
export function estimateItemsTotal(materialList = [], laborList = []) {
  const mat = (Array.isArray(materialList) ? materialList : []).reduce((s, i) => s + effectiveLinePrice(i), 0);
  const lab = (Array.isArray(laborList) ? laborList : []).reduce((s, i) => s + effectiveLinePrice(i), 0);
  return round2(mat + lab);
}
